import {
  ActionReducerMapBuilder,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import * as R from 'ramda';

import {
  CPEPlayerListenRejectedReasons,
  DEFAULT_AUTO_SAVE_INTERVAL,
  DEFAULT_PLAYBACK_SPEED_OPTIONS,
} from '@/constants';

import { PLAYER_SLICE_KEY } from './player.constants';
import { playerThunks } from './player.thunks';
import { PlayerState } from './player.types';

const {
  episodePlayPressed,
  changeActiveChapter,
  updatePlayerProgress,
  savePlayerProgress,
  seekToPosition: seekToPositionThunk,
  playPlayer,
  pausePlayer,
  resumeLastSession,
} = playerThunks;

const initialState: PlayerState = {
  isTrackLoaded: false,
  isChangingChapter: false,
  isAttemptingToChangeChapter: false,
  isPlaybackRejected: false,
  playbackRejectedChapter: undefined,
  activeChapter: undefined,
  location: undefined,
  referrer: undefined,
  isBuffering: false,
  isPlaying: false,
  progress: {
    position: undefined,
    buffered: undefined,
  },
  isExpanded: false,
  isVideoFullscreen: false,
  seekToPosition: undefined,
  isSeeking: false,
  lastSavedProgress: 0,
  isSavingProgress: false,
  playbackSpeedOptions: DEFAULT_PLAYBACK_SPEED_OPTIONS,
  autoSaveInterval: DEFAULT_AUTO_SAVE_INTERVAL,
  messages: {
    onSeek: '',
  },
};

const extraReducers = (builder: ActionReducerMapBuilder<PlayerState>) => {
  builder.addCase(episodePlayPressed.pending, state => {
    state.isAttemptingToChangeChapter = true;
  });
  builder.addCase(changeActiveChapter.pending, state => {
    state.isChangingChapter = true;
  });
  builder.addCase(updatePlayerProgress.pending, (state, { meta }) => {
    state.progress = meta.arg.progress;
  });
  builder.addCase(savePlayerProgress.pending, state => {
    state.isSavingProgress = true;
  });
  builder.addCase(savePlayerProgress.fulfilled, (state, { payload }) => {
    state.lastSavedProgress = payload.position || 0;
  });
  builder.addCase(seekToPositionThunk.pending, state => {
    state.isSeeking = true;
  });
  builder.addCase(seekToPositionThunk.fulfilled, state => {
    state.lastSavedProgress = 0;
  });
  builder.addCase(playPlayer.fulfilled, state => {
    state.isPlaying = true;
  });
  builder.addCase(pausePlayer.fulfilled, state => {
    state.isPlaying = false;
  });
  builder.addCase(changeActiveChapter.rejected, (state, { payload }) => {
    state.isChangingChapter = false;

    const { reason, episodeId, chapterId } = payload || {};

    if (
      reason === CPEPlayerListenRejectedReasons.NEED_REDEEM &&
      episodeId &&
      chapterId
    ) {
      state.isPlaybackRejected = true;
      state.playbackRejectedChapter = {
        episodeId,
        chapterId,
      };
    }
  });
  builder.addCase(changeActiveChapter.fulfilled, state => {
    state.isChangingChapter = false;
  });
  builder.addMatcher(
    isAnyOf(changeActiveChapter.fulfilled, resumeLastSession.fulfilled),
    (state, { payload }) => {
      if (!payload) return;

      const {
        activeChapter,
        autoPlay = true,
        seekToPosition,
        isExpanded,
        isVideoFullscreen,
        location,
        referrer,
      } = payload;

      state.isTrackLoaded = initialState.isTrackLoaded;
      state.isChangingChapter = initialState.isChangingChapter;
      state.isAttemptingToChangeChapter =
        initialState.isAttemptingToChangeChapter;
      state.isPlaybackRejected = initialState.isPlaybackRejected;
      state.playbackRejectedChapter = initialState.playbackRejectedChapter;
      state.isBuffering = initialState.isBuffering;
      state.progress = initialState.progress;
      state.isSavingProgress = initialState.isSavingProgress;
      state.messages = initialState.messages;

      state.activeChapter = activeChapter;
      state.seekToPosition = seekToPosition;
      state.lastSavedProgress = seekToPosition;
      state.isPlaying = autoPlay;
      state.isSeeking = !!seekToPosition;
      state.isExpanded = isExpanded || false;
      state.isVideoFullscreen = isVideoFullscreen || false;
      state.location = location;
      state.referrer = referrer;
    },
  );
  builder.addMatcher(
    isAnyOf(savePlayerProgress.rejected, savePlayerProgress.fulfilled),
    state => {
      state.isSavingProgress = false;
    },
  );
  builder.addMatcher(
    isAnyOf(seekToPositionThunk.rejected, seekToPositionThunk.fulfilled),
    state => {
      state.isSeeking = false;
      state.seekToPosition = undefined;
    },
  );
  builder.addMatcher(
    isAnyOf(episodePlayPressed.rejected, episodePlayPressed.fulfilled),
    state => {
      state.isAttemptingToChangeChapter = false;
      state.isChangingChapter = false;
    },
  );
};

export const playerSlice = createSlice({
  name: PLAYER_SLICE_KEY,
  initialState,
  reducers: {
    setBuffering: (state, { payload }: { payload: boolean }) => {
      state.isBuffering = payload;
    },
    setTrackLoaded: (state, { payload }: { payload: boolean }) => {
      state.isTrackLoaded = payload;
    },
    setSeekToPosition: (state, { payload }: { payload: number }) => {
      state.seekToPosition = payload;
      state.progress.position = payload;
    },
    clearSeekToPosition: state => {
      state.isSeeking = false;
      state.seekToPosition = undefined;
    },
    setAutoSaveInterval: (state, { payload }: { payload: number }) => {
      state.autoSaveInterval = payload;
    },
    setPlaybackSpeedOptions: (state, { payload }: { payload: number[] }) => {
      state.playbackSpeedOptions = payload;
    },
    clearActiveChapter: state => ({
      ...initialState,
      ...R.pick(['playbackSpeedOptions', 'autoSaveInterval'], state),
    }),
    setExpanded: (state, { payload }: { payload: boolean }) => {
      state.isExpanded = payload;
    },
    setVideoFullscreen: (state, { payload }: { payload: boolean }) => {
      state.isVideoFullscreen = payload;
    },
  },
  extraReducers,
});

export const playerActions = playerSlice.actions;
export const { reducer: playerReducer } = playerSlice;
export const PLAYER_SLICE_INITIAL_STATE = initialState;
